import { Action, ActionCreator, Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";
import {
	addCustomField,
	countCustomFields,
	deleteCustomFieldAsync,
	editCustomField,
	getCustomFields,
	reorderCustomField
} from "@/Services/Settings/CustomFields";

import {
	CustomField,
	CustomFieldCount,
	CustomFieldFormValues,
	ReorderCustomFieldRequest
} from "@/Contracts/Settings";
import { GlobalState } from "@/Reducers/interface";
import { ZomentumEntities } from "@/Contracts";

export enum CustomFieldsActionTypes {
	ADDRESS_CUSTOM_FIELDS_SUCCESSFUL = "ADDRESS_CUSTOM_FIELDS_SUCCESSFUL",
	OPPORTUNITY_CUSTOM_FIELDS_SUCCESSFUL = "OPPORTUNITY_CUSTOM_FIELDS_SUCCESSFUL",
	CLIENTS_CUSTOM_FIELDS_SUCCESSFUL = "CLIENTS_CUSTOM_FIELDS_SUCCESSFUL",
	CONTACTS_CUSTOM_FIELDS_SUCCESSFUL = "CONTACTS_CUSTOM_FIELDS_SUCCESSFUL",
	DOCUMENTS_CUSTOM_FIELDS_SUCCESSFUL = "DOCUMENTS_CUSTOM_FIELDS_SUCCESSFUL",
	PRODUCTS_CUSTOM_FIELDS_SUCCESSFUL = "PRODUCTS_CUSTOM_FIELDS_SUCCESSFUL",
	CALL_LOGS_CUSTOM_FIELDS_SUCCESSFUL = "CALL_LOGS_CUSTOM_FIELDS_SUCCESSFUL",
	CUSTOM_FIELDS_RESET = "CUSTOM_FIELDS_RESET"
}

export enum CountCustomFieldsActionTypes {
	COUNT_CUSTOM_FIELD_LIST_SUCCESSFUL = "COUNT_CUSTOM_FIELD_LIST_SUCCESSFUL"
}

export enum CustomFieldsOperationActionTypes {
	REORDER_CUSTOM_FIELDS_SUCCESSFUL = "REORDER_CUSTOM_FIELDS_SUCCESSFUL",
	DELETE_CUSTOM_FIELD_LIST_SUCCESSFUL = "DELETE_CUSTOM_FIELD_LIST_SUCCESSFUL",
	ADD_CUSTOM_FIELD_LIST_SUCCESSFUL = "ADD_CUSTOM_FIELD_LIST_SUCCESSFUL",
	EDIT_CUSTOM_FIELD_LIST_SUCCESSFUL = "EDIT_CUSTOM_FIELD_LIST_SUCCESSFUL"
}

export enum CustomFieldsEntityHydrationTypes {
	ADDRESS_HYDRATE = "ADDRESS_HYDRATE",
	OPPORTUNITY_HYDRATE = "OPPORTUNITY_HYDRATE",
	CLIENTS_HYDRATE = "CLIENTS_HYDRATE",
	CONTACTS_HYDRATE = "CONTACTS_HYDRATE",
	DOCUMENTS_HYDRATE = "DOCUMENTS_HYDRATE",
	PRODUCTS_HYDRATE = "PRODUCTS_HYDRATE",
	CALL_LOGS_HYDRATE = "CALL_LOGS_HYDRATE"
}

export interface CustomFieldsSuccessfulAction
	extends Action<CustomFieldsActionTypes> {
	readonly customFieldList: CustomField[];
}

export interface CustomFieldHydrationSuccessfulAction
	extends Action<CustomFieldsEntityHydrationTypes> {
	readonly customFieldEntityHydrated: boolean;
	readonly finalCustomFields?: CustomField[];
}

export interface CountCustomFieldsSuccessfulAction
	extends Action<CountCustomFieldsActionTypes> {
	readonly customFieldCount: CustomFieldCount;
}

export interface ReorderCustomFieldSuccessfulAction
	extends Action<CustomFieldsOperationActionTypes.REORDER_CUSTOM_FIELDS_SUCCESSFUL> {
	readonly customFieldList: CustomField[];
	readonly entityType: ZomentumEntities;
}

export interface DeleteCustomFieldListSuccessfulAction
	extends Action<CustomFieldsOperationActionTypes.DELETE_CUSTOM_FIELD_LIST_SUCCESSFUL> {
	readonly message: string;
}

export interface AddCustomFieldListSuccessfulAction
	extends Action<CustomFieldsOperationActionTypes.ADD_CUSTOM_FIELD_LIST_SUCCESSFUL> {
	readonly addCustomField: CustomField;
	readonly entityType: ZomentumEntities;
}

export interface EditCustomFieldListSuccessfulAction
	extends Action<CustomFieldsOperationActionTypes.EDIT_CUSTOM_FIELD_LIST_SUCCESSFUL> {
	readonly editCustomField: CustomField;
}

export type CustomFieldsResetAction =
	Action<CustomFieldsActionTypes.CUSTOM_FIELDS_RESET>;

export type CustomFieldsActions =
	| CustomFieldsSuccessfulAction
	| CountCustomFieldsSuccessfulAction
	| ReorderCustomFieldSuccessfulAction
	| DeleteCustomFieldListSuccessfulAction
	| AddCustomFieldListSuccessfulAction
	| EditCustomFieldListSuccessfulAction
	| CustomFieldsResetAction
	| CustomFieldHydrationSuccessfulAction;

export const EntityTypeToCustomFieldMapping: Record<
	Extract<
		ZomentumEntities,
		| ZomentumEntities.Address
		| ZomentumEntities.Opportunity
		| ZomentumEntities.Client
		| ZomentumEntities.ClientContact
		| ZomentumEntities.Document
		| ZomentumEntities.Product
		| ZomentumEntities.SalesLogCall
	>,
	CustomFieldsActionTypes
> = {
	[ZomentumEntities.Address]:
		CustomFieldsActionTypes.ADDRESS_CUSTOM_FIELDS_SUCCESSFUL,
	[ZomentumEntities.Opportunity]:
		CustomFieldsActionTypes.OPPORTUNITY_CUSTOM_FIELDS_SUCCESSFUL,
	[ZomentumEntities.Client]:
		CustomFieldsActionTypes.CLIENTS_CUSTOM_FIELDS_SUCCESSFUL,
	[ZomentumEntities.ClientContact]:
		CustomFieldsActionTypes.CONTACTS_CUSTOM_FIELDS_SUCCESSFUL,
	[ZomentumEntities.Document]:
		CustomFieldsActionTypes.DOCUMENTS_CUSTOM_FIELDS_SUCCESSFUL,
	[ZomentumEntities.Product]:
		CustomFieldsActionTypes.PRODUCTS_CUSTOM_FIELDS_SUCCESSFUL,
	[ZomentumEntities.SalesLogCall]:
		CustomFieldsActionTypes.CALL_LOGS_CUSTOM_FIELDS_SUCCESSFUL
};

export const HydrateEntityTypeToCustomFieldMapping: Record<
	Extract<
		ZomentumEntities,
		| ZomentumEntities.Address
		| ZomentumEntities.Opportunity
		| ZomentumEntities.Client
		| ZomentumEntities.ClientContact
		| ZomentumEntities.Document
		| ZomentumEntities.Product
		| ZomentumEntities.SalesLogCall
	>,
	CustomFieldsEntityHydrationTypes
> = {
	[ZomentumEntities.Address]:
		CustomFieldsEntityHydrationTypes.ADDRESS_HYDRATE,
	[ZomentumEntities.Opportunity]:
		CustomFieldsEntityHydrationTypes.OPPORTUNITY_HYDRATE,
	[ZomentumEntities.Client]: CustomFieldsEntityHydrationTypes.CLIENTS_HYDRATE,
	[ZomentumEntities.ClientContact]:
		CustomFieldsEntityHydrationTypes.CONTACTS_HYDRATE,
	[ZomentumEntities.Document]:
		CustomFieldsEntityHydrationTypes.DOCUMENTS_HYDRATE,
	[ZomentumEntities.Product]:
		CustomFieldsEntityHydrationTypes.PRODUCTS_HYDRATE,
	[ZomentumEntities.SalesLogCall]:
		CustomFieldsEntityHydrationTypes.CALL_LOGS_HYDRATE
};

export interface CustomFieldsDispatch {
	getCustomFields: ActionCreator<
		ThunkAction<
			Promise<CustomFieldsSuccessfulAction>,
			GlobalState,
			ZomentumEntities,
			CustomFieldsSuccessfulAction
		>
	>;
	countCustomFields: ActionCreator<
		ThunkAction<
			Promise<CountCustomFieldsSuccessfulAction>,
			GlobalState,
			null,
			CountCustomFieldsSuccessfulAction
		>
	>;
	reorderCustomFields: ActionCreator<
		ThunkAction<
			Promise<ReorderCustomFieldSuccessfulAction>,
			GlobalState,
			ReorderCustomFieldRequest,
			ReorderCustomFieldSuccessfulAction
		>
	>;
	deleteCustomField: ActionCreator<
		ThunkAction<
			Promise<DeleteCustomFieldListSuccessfulAction>,
			GlobalState,
			CustomField,
			DeleteCustomFieldListSuccessfulAction
		>
	>;
	addCustomField: ActionCreator<
		ThunkAction<
			Promise<AddCustomFieldListSuccessfulAction>,
			GlobalState,
			CustomFieldFormValues,
			AddCustomFieldListSuccessfulAction
		>
	>;
	editCustomField: ActionCreator<
		ThunkAction<
			Promise<EditCustomFieldListSuccessfulAction>,
			GlobalState,
			CustomFieldFormValues,
			EditCustomFieldListSuccessfulAction
		>
	>;
	resetCustomFields: ActionCreator<
		ThunkAction<
			CustomFieldsResetAction,
			GlobalState,
			null,
			CustomFieldsResetAction
		>
	>;
	hydrateCustomFields: ActionCreator<
		ThunkAction<
			CustomFieldHydrationSuccessfulAction,
			GlobalState,
			ZomentumEntities,
			CustomFieldHydrationSuccessfulAction
		>
	>;
	unhydrateCustomFields: ActionCreator<
		ThunkAction<
			CustomFieldHydrationSuccessfulAction,
			GlobalState,
			ZomentumEntities,
			CustomFieldHydrationSuccessfulAction
		>
	>;
}

export const CustomFieldsActionCreators: CustomFieldsDispatch = {
	getCustomFields:
		(
			entityType: Extract<
				ZomentumEntities,
				| ZomentumEntities.Address
				| ZomentumEntities.Opportunity
				| ZomentumEntities.Client
				| ZomentumEntities.ClientContact
				| ZomentumEntities.Document
				| ZomentumEntities.Product
				| ZomentumEntities.SalesLogCall
			>
		) =>
		async (dispatch: Dispatch): Promise<CustomFieldsSuccessfulAction> => {
			try {
				const getCustomFieldsResponse = await getCustomFields(
					entityType
				);

				const getCustomFieldsSuccessfulAction: CustomFieldsSuccessfulAction =
					{
						type: EntityTypeToCustomFieldMapping[entityType],
						customFieldList: getCustomFieldsResponse.data
					};
				return dispatch(getCustomFieldsSuccessfulAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		},
	countCustomFields:
		() =>
		async (
			dispatch: Dispatch
		): Promise<CountCustomFieldsSuccessfulAction> => {
			try {
				const getCountCustomFieldsResponse = await countCustomFields();

				const getCountCustomFieldsSuccessfulAction: CountCustomFieldsSuccessfulAction =
					{
						type: CountCustomFieldsActionTypes.COUNT_CUSTOM_FIELD_LIST_SUCCESSFUL,
						customFieldCount: getCountCustomFieldsResponse
					};
				return dispatch(getCountCustomFieldsSuccessfulAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		},
	reorderCustomFields:
		(reorderCustomFieldRequest: ReorderCustomFieldRequest) =>
		async (
			dispatch: Dispatch
		): Promise<ReorderCustomFieldSuccessfulAction> => {
			try {
				await reorderCustomField(
					reorderCustomFieldRequest.fields,
					reorderCustomFieldRequest.entityType
				);

				const reorderCustomFieldSuccessfulAction: ReorderCustomFieldSuccessfulAction =
					{
						type: CustomFieldsOperationActionTypes.REORDER_CUSTOM_FIELDS_SUCCESSFUL,
						customFieldList: reorderCustomFieldRequest.fields,
						entityType: reorderCustomFieldRequest.entityType
					};
				return dispatch(reorderCustomFieldSuccessfulAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		},
	deleteCustomField: (deleteCustomFieldListRequest: CustomField) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteCustomFieldListSuccessfulAction> => {
			try {
				const deleteCustomFieldListResponse =
					await deleteCustomFieldAsync(deleteCustomFieldListRequest);
				const deleteCustomFieldListSuccessfulAction: DeleteCustomFieldListSuccessfulAction =
					{
						type: CustomFieldsOperationActionTypes.DELETE_CUSTOM_FIELD_LIST_SUCCESSFUL,
						message: deleteCustomFieldListResponse
					};
				return dispatch(deleteCustomFieldListSuccessfulAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		};
	},
	addCustomField: (addCustomFieldListRequest: CustomFieldFormValues) => {
		return async (
			dispatch: Dispatch
		): Promise<AddCustomFieldListSuccessfulAction> => {
			try {
				const addCustomFieldListResponse = await addCustomField(
					addCustomFieldListRequest
				);
				const addCustomFieldListSuccessfulAction: AddCustomFieldListSuccessfulAction =
					{
						type: CustomFieldsOperationActionTypes.ADD_CUSTOM_FIELD_LIST_SUCCESSFUL,
						addCustomField: addCustomFieldListResponse,
						entityType: addCustomFieldListRequest.entity_type
					};
				return dispatch(addCustomFieldListSuccessfulAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		};
	},
	editCustomField: (editCustomFieldListRequest: CustomFieldFormValues) => {
		return async (
			dispatch: Dispatch
		): Promise<EditCustomFieldListSuccessfulAction> => {
			try {
				const editCustomFieldListResponse = await editCustomField(
					editCustomFieldListRequest
				);
				const editCustomFieldListSuccessfulAction: EditCustomFieldListSuccessfulAction =
					{
						type: CustomFieldsOperationActionTypes.EDIT_CUSTOM_FIELD_LIST_SUCCESSFUL,
						editCustomField: editCustomFieldListResponse
					};
				return dispatch(editCustomFieldListSuccessfulAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		};
	},
	resetCustomFields: () => {
		return (dispatch: Dispatch): CustomFieldsResetAction => {
			try {
				const customFieldsResetAction: CustomFieldsResetAction = {
					type: CustomFieldsActionTypes.CUSTOM_FIELDS_RESET
				};
				return dispatch(customFieldsResetAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		};
	},
	hydrateCustomFields:
		(
			entityType: Extract<
				ZomentumEntities,
				| ZomentumEntities.Opportunity
				| ZomentumEntities.Client
				| ZomentumEntities.ClientContact
				| ZomentumEntities.Document
				| ZomentumEntities.Product
				| ZomentumEntities.SalesLogCall
			>,
			allCustomFieldList: CustomField[],
			entityCustomFieldsWithValues: CustomField[]
		) =>
		(dispatch: Dispatch): CustomFieldHydrationSuccessfulAction => {
			try {
				const customFieldsList = allCustomFieldList.filter(
					customField => !customField.is_system_field
				);
				const existingCustomFieldsWithValues = {};
				entityCustomFieldsWithValues.forEach(customField => {
					existingCustomFieldsWithValues[customField.id] =
						customField.values;
				});
				const newCustomFieldsList: CustomField[] = [];
				customFieldsList.forEach(currentCustomField => {
					if (!currentCustomField.is_system_field) {
						if (
							currentCustomField.id in
							existingCustomFieldsWithValues
						) {
							const customFieldValue = {
								...currentCustomField,
								values: existingCustomFieldsWithValues[
									currentCustomField.id
								]
							};
							newCustomFieldsList.push(customFieldValue);
						} else {
							const customFieldValue = {
								...currentCustomField,
								values: []
							};
							newCustomFieldsList.push(customFieldValue);
						}
					}
				});

				const customFieldEntityHydratedAction: CustomFieldHydrationSuccessfulAction =
					{
						type: HydrateEntityTypeToCustomFieldMapping[entityType],
						customFieldEntityHydrated: true,
						finalCustomFields: newCustomFieldsList
					};
				return dispatch(customFieldEntityHydratedAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		},
	unhydrateCustomFields:
		(
			entityType: Extract<
				ZomentumEntities,
				| ZomentumEntities.Opportunity
				| ZomentumEntities.Client
				| ZomentumEntities.ClientContact
				| ZomentumEntities.Document
				| ZomentumEntities.Product
				| ZomentumEntities.SalesLogCall
			>
		) =>
		(dispatch: Dispatch): CustomFieldHydrationSuccessfulAction => {
			try {
				const customFieldEntityHydratedAction: CustomFieldHydrationSuccessfulAction =
					{
						type: HydrateEntityTypeToCustomFieldMapping[entityType],
						customFieldEntityHydrated: false
					};
				return dispatch(customFieldEntityHydratedAction);
			} catch (error) {
				console.error(error);
				throw error;
			}
		}
};
